import React, { useState, useEffect, useRef, useMemo } from 'react';
import WebFont from 'webfontloader';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import * as Store from '@apply/store';
import { Header } from './Header';
import { useAuthentication } from './LoginPage';
import { alertModal, EsignModal, validEmail, validPhone, Font, fonts } from '@apply/ui';
import { UserStore } from '@apply/store';
import debounce from 'lodash.debounce';
import { PhoneInput } from '@apply/ui';
import { TextBox } from 'devextreme-react/text-box';
import { Validator, RequiredRule, PatternRule } from 'devextreme-react/validator';
import { ValidationGroup } from 'devextreme-react/validation-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { HomePageLink } from './HomePageLink';

export interface Actions {
    validateEmail?: (email: string, userType: UserStore.Types.UserType) => Promise<UserStore.Types.ValidateEmailResult>;
    registerPrincipal?: (
        id: string,
        email: string,
        firstName: string,
        middleName: string,
        lastName: string,
        mobilePhone: string,
        secondaryEmail: string,
        sSignFontId: number,
        ipAddress: string
    ) => Promise<UserStore.Types.RegisterUserResult>;
    requestBrokerProfile?: (
        email: string,
        firstName: string,
        middleName: string,
        lastName: string,
        mobilePhone: string
    ) => Promise<UserStore.Types.RegisterUserResult>;
    onNeedIpAddress?: () => Promise<string>;
    getUserProfile?: (id: string, token: string) => Promise<Store.CrossStore.UserProfileResult>;
}

export interface RegisterPageProps {
    actions?: Actions;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const getElectronicRecordsHtml = async () => await Store.CrossStore.getElectronicRecordsHtml();

export const RegisterPage: React.FC<RegisterPageProps> = ({ actions }) => {
    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [middleName, setMiddleName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const fullname = useMemo(
        () => [firstName?.trim(), middleName?.trim(), lastName?.trim()].filter((np) => !!np).join(' '),
        [firstName, middleName, lastName]
    );
    const [mobilePhone, setMobilePhone] = useState<string>('');
    const [secondaryEmail, setSecondaryEmail] = useState<string>('');
    const [acknowledgeNoPersonalLoans, setAcknowledgeNoPersonalLoans] = useState<boolean>(false);
    const [agreeToEsignAgreement, setAgreeToEsignAgreement] = useState<boolean>(false);

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [errorModal, setErrorModal] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [successful, setSuccessful] = useState<boolean>(false);

    const [emailUnavailable, setEmailUnavailable] = useState<boolean>(false);
    const [accountExists, setAccountExists] = useState<boolean>(false);
    const [targetBaseUrl, setTargetBaseUrl] = useState<string>(window.location.origin);

    const [ipAddress, setIpAddress] = useState<string>('');
    const [esignHtml, set_esignHtml] = useState<string>('');
    const [agreementOpen, setAgreementOpen] = useState<boolean>(false);
    const [eSignFontId, setESignFontId] = useState<string>('');

    //0 = Not set, 1 = Applicant, 2 == Broker
    const enum RegisterType {
        NotSet = 0,
        Applicant = 1,
        Broker = 2,
    }
    const [registerType, set_registerType] = useState<RegisterType>(RegisterType.NotSet);

    const mounted = useRef<boolean>(true);

    const vgRef = useRef<ValidationGroup>(null);

    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const userId = params.get('id');

    const token = params.get('code');

    const { isAuthenticated } = useAuthentication();

    const emailPattern =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    useEffect(() => {
        (async () => {
            const fontNames = fonts.map((font) => font.name);
            WebFont.load({
                google: {
                    families: fontNames,
                },
            });
        })();
    }, []);

    useEffect(() => {
        let canceled = false;

        if (actions?.onNeedIpAddress) {
            actions.onNeedIpAddress().then((ip) => {
                if (!canceled) {
                    setIpAddress(ip);
                }
            });
        }

        return () => {
            canceled = true;
        };
    }, [actions]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            debouncedEmailValidation.cancel();
        };
    }, []);

    useEffect(() => {
        if (email) {
            handleEmailValidation(email);
        }
    }, [registerType]);

    const handlePrincipalRegister = async () => {
        /* istanbul ignore if */
        if (!validEmail(email) || !firstName || !lastName || !validPhone(mobilePhone)) {
            return;
        }

        setSubmitting(true);
        setErrorModal(false);
        setErrorMessage('');
        try {
            const result = await actions.registerPrincipal(
                userId,
                email,
                firstName,
                middleName,
                lastName,
                mobilePhone,
                secondaryEmail,
                parseInt(eSignFontId),
                ipAddress
            );

            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                if (result.status >= 0) {
                    //Interpret the result
                    setTargetBaseUrl(result.baseUrl);
                    switch (result.status) {
                        case UserStore.Types.RegisterUserResultStatus.AccountExists:
                            setAccountExists(true);
                            break;
                        case UserStore.Types.RegisterUserResultStatus.RequestSuccessful:
                            setSuccessful(true);
                            break;
                        default:
                            setEmailUnavailable(true);
                            break;
                    }
                } else {
                    setErrorModal(true);
                    setErrorMessage('Register Failed');
                }
            }
        } catch (err) {
            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                setErrorModal(true);
                setErrorMessage(err.message);
            }
        }
    };

    const handleBrokerRegister = async () => {
        /* istanbul ignore if */
        if (!validEmail(email) || !firstName || !lastName || !validPhone(mobilePhone)) {
            return;
        }

        setSubmitting(true);
        setErrorModal(false);
        setErrorMessage('');
        try {
            const result = await actions.requestBrokerProfile(email, firstName, middleName, lastName, mobilePhone);
            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                if (result.status >= 0) {
                    //Interpret the result
                    setTargetBaseUrl(result.baseUrl);
                    switch (result.status) {
                        case UserStore.Types.RegisterUserResultStatus.AccountExists:
                            setAccountExists(true);
                            break;
                        case UserStore.Types.RegisterUserResultStatus.RequestSuccessful:
                            setSuccessful(true);
                            break;
                        default:
                            setEmailUnavailable(true);
                            break;
                    }
                } else {
                    setErrorModal(true);
                    setErrorMessage('Broker Profile request failed');
                }
            }
        } catch (err) {
            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                setErrorModal(true);
                setErrorMessage(err.message);
            }
        }
    };

    const handleEmailValidation = async (email) => {
        /* istanbul ignore if */
        if (!email) {
            return;
        }

        setAccountExists(false);
        setEmailUnavailable(false);
        setSubmitting(true);
        setErrorModal(false);
        setErrorMessage('');
        try {
            const result = await actions.validateEmail(
                email,
                registerType === RegisterType.Applicant ? UserStore.Types.UserType.Principal : UserStore.Types.UserType.Broker
            );

            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                if (result.status >= 0) {
                    //Interpret the result
                    setTargetBaseUrl(result.baseUrl);
                    switch (result.status) {
                        case UserStore.Types.ValidateEmailResultStatus.AccountExists:
                            setAccountExists(true);
                            break;
                        case UserStore.Types.ValidateEmailResultStatus.Unavailable:
                            setEmailUnavailable(true);
                            break;
                    }
                } else {
                    setErrorModal(true);
                    setErrorMessage('Register Failed');
                }
            }
        } catch (err) {
            // istanbul ignore else
            if (mounted) {
                setSubmitting(false);
                setErrorModal(true);
                setErrorMessage(err.message);
            }
        }
    };

    const debouncedEmailValidation = debounce(handleEmailValidation, 500);

    useEffect(() => {
        if (errorModal && errorMessage) {
            alertModal({
                message:
                    registerType === RegisterType.Broker
                        ? `Unable to complete Broker Profile request: <br><br><ul style="color:red"><li>${errorMessage}</li></ul></>`
                        : `Unable to complete Account registration: <br><br><ul style="color:red"><li>${errorMessage}</li></ul></>`,
                onConfirm: () => {
                    setErrorModal(false);
                },
            });
        }
    }, [errorMessage]);

    useEffect(() => {
        setEmailUnavailable(false);
        setAccountExists(false);

        if (email) {
            const isValid = validEmail(email);
            if (isValid) {
                debouncedEmailValidation(email);
            }
        }
    }, [email]);

    useEffect(() => {
        if (!userId || !token) {
            return;
        }

        //Is an existing user so retrieve their profile details and let them edit them
        actions.getUserProfile(userId, token).then((profile) => {
            setEmail(profile.email ?? '');
            setFirstName(profile.firstName ?? '');
            setMiddleName(profile.middleInitial ?? '');
            setLastName(profile.lastName ?? '');
            setMobilePhone(profile.phone ?? '');
        });
    }, [userId, token, actions]);

    const onConfirmAgreement = (font: Font) => {
        setAgreementOpen(false);
        setESignFontId(font.id);
        setAgreeToEsignAgreement(true);
    };

    const onRejectAgreement = () => {
        setAgreementOpen(false);
        setAgreeToEsignAgreement(false);
    };

    const onClickESignAgreement = async () => {
        const html = await getElectronicRecordsHtml();
        set_esignHtml(html);
        setAgreementOpen(true);
    };

    const getLoginUrl = () => {
        switch (registerType) {
            case RegisterType.Broker:
                return `${targetBaseUrl}/login?email=${encodeURIComponent(email)}`;
            default:
                return `/login?email=${encodeURIComponent(email)}`;
        }
    };

    const getResetPasswordUrl = () => {
        switch (registerType) {
            case RegisterType.Broker:
                return `${targetBaseUrl}/forgotPassword?email=${encodeURIComponent(email)}`;
            default:
                return `/forgotPassword?email=${encodeURIComponent(email)}`;
        }
    };

    if (successful) {
        if (registerType === RegisterType.Broker) {
            return (
                <>
                    <Header />
                    <div className='app-container login apply-login-container'>
                        <h2>Thank You</h2>
                        <main>
                            <div className='simple-form-container one-column'>
                                Please keep an eye out for a registration link for the Broker Portal.
                            </div>
                        </main>
                    </div>
                </>
            );
        }
        if (registerType === RegisterType.Applicant) {
            return (
                <>
                    <Header />
                    <div className='app-container login apply-login-container'>
                        <h2>Thank you for registering</h2>
                        <main>
                            <div className='simple-form-container one-column'>Please check your email for the next steps...</div>
                        </main>
                    </div>
                </>
            );
        }
    }

    const isFormCompleted = () => {
        let inValid = false;
        if (registerType === RegisterType.Applicant) {
            inValid = !validEmail(email) || !firstName || !lastName || !validPhone(mobilePhone);
            if (secondaryEmail) {
                inValid = inValid || !validEmail(secondaryEmail);
            }
        } else {
            inValid = !validEmail(email) || !firstName || !lastName || !validPhone(mobilePhone);
        }

        if (inValid) {
            return false;
        }

        return true;
    };

    const canSubmit = () => {
        let inValid = !isFormCompleted();
        if (!inValid) {
            if (registerType === RegisterType.Applicant) {
                inValid = !acknowledgeNoPersonalLoans || emailUnavailable || accountExists;
            } else {
                inValid = emailUnavailable || accountExists;
            }
        }
        return inValid ? false : true;
    };

    const submitChanges = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const validation = vgRef.current?.instance?.validate();
        if (!validation?.isValid) {
            return;
        }

        if (registerType === RegisterType.Applicant) {
            handlePrincipalRegister();
        } else {
            handleBrokerRegister();
        }
    };

    if (isAuthenticated) {
        return (
            <>
                <Header />
                <div className='app-container login apply-login-container'>
                    <main>
                        <HomePageLink />
                        <br />
                        <div style={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            Please log out to register a new account.
                        </div>
                    </main>
                </div>
            </>
        );
    }
    return (
        <>
            <Header />
            <div className='app-container login apply-login-container'>
                <main>
                    <HomePageLink />
                    <h2>Register Your Account</h2>
                    <div className='simple-form-container one-column'>
                        <div className='columns form-content'>
                            <form
                                name='orderAppraisalForm'
                                onSubmit={(event) => submitChanges(event)}
                            >
                                <ValidationGroup
                                    id='vg1'
                                    ref={vgRef}
                                >
                                    <div className='standard-form'>
                                        <div className='row'>
                                            <div className='columns small-12 large-12'>I am a...</div>
                                            <div className='columns small-6 large-6'>
                                                <input
                                                    name='isBroker'
                                                    type='radio'
                                                    value={RegisterType.Applicant}
                                                    checked={registerType === RegisterType.Applicant}
                                                    onChange={(e) => set_registerType(parseInt(e.target.value))}
                                                />
                                                &nbsp;&nbsp;Applicant
                                            </div>
                                            <div className='columns small-6 large-6'>
                                                <input
                                                    name='isBroker'
                                                    type='radio'
                                                    value={RegisterType.Broker}
                                                    checked={registerType === RegisterType.Broker}
                                                    onChange={(e) => set_registerType(parseInt(e.target.value))}
                                                />
                                                &nbsp;&nbsp;Broker
                                            </div>

                                            {registerType !== RegisterType.NotSet && (
                                                <>
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='emailAddress'>
                                                            Email Address
                                                            <span className='req'>*</span>
                                                        </label>
                                                        <TextBox
                                                            id='emailAddress'
                                                            data-testid='register-page-email-address'
                                                            value={email}
                                                            maxLength={255}
                                                            disabled={!!userId}
                                                            validationMessageMode='always'
                                                            onValueChanged={(e) => setEmail(e.value)}
                                                        >
                                                            <Validator>
                                                                <RequiredRule message='Email is required' />
                                                                <PatternRule
                                                                    message='Email is invalid'
                                                                    pattern={emailPattern}
                                                                />
                                                            </Validator>
                                                        </TextBox>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>
                                                                Enter your email address, i.e. johnsmith@email.com
                                                            </span>
                                                        </span>
                                                    </div>
                                                    {emailUnavailable && (
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={{
                                                                border: '1px solid darkred',
                                                                borderRadius: '5px',
                                                                padding: '5px',
                                                                marginBottom: '10px',
                                                            }}
                                                        >
                                                            <Typography
                                                                style={{
                                                                    color: 'darkred',
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                                &nbsp;&nbsp;Sorry, but this email address is not available for
                                                                registration.
                                                            </Typography>
                                                        </div>
                                                    )}
                                                    {accountExists && (
                                                        <div
                                                            className='columns small-12 large-12'
                                                            style={{
                                                                border: '1px solid darkred',
                                                                borderRadius: '5px',
                                                                padding: '5px',
                                                                marginBottom: '10px',
                                                            }}
                                                        >
                                                            <Typography
                                                                style={{
                                                                    color: 'darkred',
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                                &nbsp;&nbsp;An account with this email already exists. Please
                                                                select from one of the options below.
                                                                <br />
                                                            </Typography>
                                                            <div
                                                                style={{
                                                                    marginLeft: '10px',
                                                                }}
                                                            >
                                                                <ul>
                                                                    <li>
                                                                        <a href={getLoginUrl()}>Go to Login page</a>
                                                                    </li>{' '}
                                                                    <li>
                                                                        <a href={getResetPasswordUrl()}>Request Password Reset</a>
                                                                    </li>
                                                                    <li>Change the email address</li>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='firstName'>
                                                            Legal First Name
                                                            <span className='req'>*</span>
                                                        </label>
                                                        <TextBox
                                                            id='firstName'
                                                            data-testid='register-page-first-name'
                                                            value={firstName}
                                                            maxLength={50}
                                                            validationMessageMode='always'
                                                            onValueChanged={(e) => setFirstName(e.value)}
                                                        >
                                                            <Validator>
                                                                <RequiredRule message='First Name is required' />
                                                            </Validator>
                                                        </TextBox>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Enter your full legal first name</span>
                                                        </span>
                                                    </div>
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='middleName'>Middle Initial or Name (optional)</label>
                                                        <TextBox
                                                            id='middleName'
                                                            data-testid='register-page-middle-name'
                                                            value={middleName}
                                                            maxLength={50}
                                                            onValueChanged={(e) => setMiddleName(e.value)}
                                                        ></TextBox>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Enter your middle initial/name</span>
                                                        </span>
                                                    </div>
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='lastName'>
                                                            Legal Last Name
                                                            <span className='req'>*</span>
                                                        </label>
                                                        <TextBox
                                                            id='lastName'
                                                            data-testid='register-page-last-name'
                                                            value={lastName}
                                                            maxLength={50}
                                                            validationMessageMode='always'
                                                            onValueChanged={(e) => setLastName(e.value)}
                                                        >
                                                            <Validator>
                                                                <RequiredRule message='Last Name is required' />
                                                            </Validator>
                                                        </TextBox>
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>Enter your full legal last name</span>
                                                        </span>
                                                    </div>
                                                    <div className='columns small-12 large-12'>
                                                        <label htmlFor='mobilePhone'>
                                                            Mobile Phone Number
                                                            <span className='req'>*</span>
                                                        </label>
                                                        <PhoneInput
                                                            id='mobilePhone'
                                                            data-testid='register-page-mobile-phone'
                                                            value={mobilePhone}
                                                            onChange={(phone) => setMobilePhone(phone)}
                                                            required={true}
                                                        />
                                                        <span className='helper'>
                                                            <span className='tooltip-trigger'>
                                                                <FontAwesomeIcon icon={faCircleQuestion} />
                                                            </span>
                                                            <span className='text'>
                                                                Enter your mobile phone number including area code, i.e. (610)
                                                                555-1212
                                                            </span>
                                                        </span>
                                                    </div>
                                                    {registerType === RegisterType.Applicant && (
                                                        <>
                                                            <div className='columns small-12 large-12'>
                                                                <label htmlFor='secondaryEmail'>
                                                                    Secondary Email Address (optional)
                                                                </label>
                                                                <TextBox
                                                                    id='secondaryEmail'
                                                                    data-testid='register-page-secondary-email'
                                                                    value={secondaryEmail}
                                                                    maxLength={255}
                                                                    disabled={!!userId}
                                                                    validationMessageMode='always'
                                                                    onValueChanged={(e) => setSecondaryEmail(e.value)}
                                                                >
                                                                    <Validator>
                                                                        <PatternRule
                                                                            message='Email is invalid'
                                                                            pattern={emailPattern}
                                                                        />
                                                                    </Validator>
                                                                </TextBox>
                                                                <span className='helper'>
                                                                    <span className='tooltip-trigger'>
                                                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                                                    </span>
                                                                    <span className='text'>
                                                                        Enter an optional secondary email address, i.e.
                                                                        johnsmith@email.com
                                                                    </span>
                                                                </span>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-12'
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'row',
                                                                }}
                                                            >
                                                                <div>
                                                                    <input
                                                                        id='acknowledgeNoPersonalLoans'
                                                                        data-testid='register-page-acknowledge-no-personal-loans'
                                                                        type='checkbox'
                                                                        checked={acknowledgeNoPersonalLoans}
                                                                        onChange={(e) =>
                                                                            setAcknowledgeNoPersonalLoans(e.target.checked)
                                                                        }
                                                                        required
                                                                    />
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        marginLeft: '10px',
                                                                    }}
                                                                >
                                                                    <label
                                                                        htmlFor='acknowledgeNoPersonalLoans'
                                                                        style={{
                                                                            fontWeight: 'bold',
                                                                        }}
                                                                    >
                                                                        I acknowledge that this application is for a BUSINESS
                                                                        PURPOSE
                                                                        <br />
                                                                        AND that the property is or will be OWNED BY A CORPORATE
                                                                        ENTITY. occupied property.
                                                                    </label>
                                                                </div>
                                                            </div>
                                                            <div
                                                                className='columns small-12 large-12'
                                                                style={{
                                                                    display: 'flex',
                                                                    flexDirection: 'row',
                                                                }}
                                                            >
                                                                <div>
                                                                    <input
                                                                        id='agreeToEsignAgreement'
                                                                        data-testid='register-page-agree-to-esign-agreement'
                                                                        type='checkbox'
                                                                        checked={agreeToEsignAgreement}
                                                                        required
                                                                        // need a no-op onChange because otherwise React gripes a bunch in the console
                                                                        onChange={noop}
                                                                        onClick={onClickESignAgreement}
                                                                        disabled={!isFormCompleted()}
                                                                    />
                                                                </div>
                                                                <div
                                                                    style={{
                                                                        marginLeft: '10px',
                                                                    }}
                                                                >
                                                                    <label
                                                                        htmlFor='agreeToEsignAgreement'
                                                                        style={{
                                                                            fontWeight: 'bold',
                                                                        }}
                                                                    >
                                                                        Consent to Use Electronic Records and Disclosures. By
                                                                        checking this check box and clicking "REGISTER", I
                                                                        acknowledge that I have read and agree on behalf of the
                                                                        Business Entities to the Consent to Use Electronic Records
                                                                        and Disclosures.
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}

                                                    {errorMessage && (
                                                        <div className='columns small-12 large-12'>
                                                            <Typography
                                                                style={{
                                                                    color: 'red',
                                                                }}
                                                            >
                                                                {errorMessage}
                                                            </Typography>
                                                        </div>
                                                    )}

                                                    {registerType === RegisterType.Applicant && (
                                                        <>
                                                            <div className='columns small-12 large-12'>
                                                                <button
                                                                    type='submit'
                                                                    data-testid='register-page-button'
                                                                    disabled={
                                                                        submitting || !agreeToEsignAgreement || !canSubmit()
                                                                    }
                                                                    className='button signin-button'
                                                                >
                                                                    Register
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                    {registerType === RegisterType.Broker && (
                                                        <div className='columns small-12 large-12'>
                                                            <button
                                                                type='submit'
                                                                data-testid='register-page-button'
                                                                disabled={submitting || !canSubmit()}
                                                                className='button signin-button'
                                                            >
                                                                Request Broker Profile
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </ValidationGroup>
                            </form>
                        </div>
                    </div>
                </main>
            </div>
            {agreementOpen && (
                <EsignModal
                    agreementHtml={esignHtml}
                    isOpen={agreementOpen}
                    signatureText={fullname}
                    cancelCallback={onRejectAgreement}
                    confirmCallback={onConfirmAgreement}
                    ipAddress={ipAddress}
                    rejectLabel='I DO NOT ACCEPT THIS AGREEMENT'
                    acceptLabel='I ACCEPT THIS AGREEMENT'
                />
            )}
        </>
    );
};

export const mapDispatchToProps = (dispatch: Store.ApplyDispatchType) => {
    const props: { actions: Actions; dispatch: Store.ApplyDispatchType } = {
        actions: {
            validateEmail: (email: string, userType: UserStore.Types.UserType) =>
                dispatch(Store.CrossStore.validateEmail(email, userType)),
            registerPrincipal: (
                id: string,
                email: string,
                firstName: string,
                middleName: string,
                lastName: string,
                mobilePhone: string,
                secondaryEmail: string,
                eSignFontId: number,
                ipAddress: string
            ) =>
                dispatch(
                    Store.CrossStore.register(
                        id,
                        email,
                        firstName,
                        middleName,
                        lastName,
                        mobilePhone,
                        secondaryEmail,
                        eSignFontId,
                        ipAddress
                    )
                ),
            requestBrokerProfile: (email: string, firstName: string, middleName: string, lastName: string, mobilePhone: string) =>
                dispatch(Store.CrossStore.requestBrokerProfile(email, firstName, middleName, lastName, mobilePhone)),
            onNeedIpAddress: () => dispatch(Store.CrossStore.getIpAddress()),
            getUserProfile: (id: string, token: string) => dispatch(Store.CrossStore.getUserProfile(id, token)),
        },
        dispatch,
    };
    return props;
};

export default connect(null, mapDispatchToProps)(RegisterPage);
